// The Sales Order packing plan's staging rows (moved out of pages/SalesOrderPage.jsx word for word, 2026-10-27) and
// "Prepare shipment" (jobq-api docs/holding-pallets-design.md section 4): the Staging queue's view when the plan has
// holding pallets (laser task WIP pallets). One card per holding pallet: where it stands (STAGE FOR BRAKE / Ready to
// pack), its pieces per part (cut vs still being cut), its beacon + Find, and the two ways out: "Ship this pallet as
// it is" (it becomes the shipping pallet) and "Pack into…" (today's assign rows). Rules: features/salesOrders/
// holdingPallets.js. The page owns every request (onPromote, onManualAssign, onCreatePallet, onClearWip, onFind).
// Each card also carries Check pallet and Move pieces (Mike 2026-10-29, SO-1145: moved up from the pallet card below);
// HoldingMoveDialog makes its own requests and calls onMoved after a move.
import React from 'react';
import { ProductIdLink } from '../PartDetailModal.jsx';
import HoldingMoveDialog from '../HoldingMoveDialog.jsx';
import PalletCheckLink from '../../features/palletCheck/PalletCheckLink.jsx';
import { openProblemsFor } from '../../features/palletCheck/palletCheckData';
import { onWipText } from '../../features/salesOrders/palletRules';
import {
  holdingPallets, holdingStationState, holdingPalletParts, piecesText, promoteState, rowUnits, stagedOffHolding,
} from '../../features/salesOrders/holdingPallets';

// A staged part's pieces on laser task WIP pallets (load_plan `on_wip`), each with Clear WIP: under its
// Staging-queue row, and on a Complete plan too (a plan can complete with a WIP pallet still holding pieces).
export const SOStagingOnWip = ({ onWip = [], busy = '', onClearWip }) => {
  if (!onWip.length) return null;
  return (
    <div className="w-100 ps-4 d-flex flex-column gap-1">
      {onWip.map((w) => (
        <div key={w.pallet_id} className="d-flex align-items-center gap-2 text-muted">
          <span title="On a laser task's WIP pallet. Assigning takes the pieces off it, oldest WIP pallet first.">
            <i className="bi bi-arrow-right-circle me-1"></i>{onWipText(w)}
          </span>
          {onClearWip && (
            <button
              className="btn btn-sm btn-outline-danger py-0"
              disabled={!!busy}
              title="Only when these pieces went somewhere JobQ did not record (packed by hand, scrapped): every stack on the WIP pallet goes to 0 and the pallet ends."
              onClick={() => onClearWip(w.pallet_id, w.short_id || `pallet ${w.pallet_id}`)}
            >
              {busy === `wip_clear_${w.pallet_id}`
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Clearing…</>
                : 'Clear WIP'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// Two optional props for Prepare shipment (2026-10-27; without them the row is the Staging queue's, as before):
// `extraBody` is merged into the manual-assign body (a holding pallet card sends from_wip_pallet_id), and `ppq` > 1
// makes the quantity box count whole ASSEMBLIES of an assembly stack (sent as ppq × N primary pieces).
export const SOStagingAssignRow = ({ part, destOptions, newPalletValue, busy = '', onManualAssign, onCreatePallet, onInspectPart, onClearWip, extraBody = null, ppq = 1 }) => {
  // remaining_qty (server-computed: SO ordered minus every zone already on
  // the plan, incl. shipped boxes) is what's actually left to assign — a
  // part partially covered earlier (e.g. 1 of 2 via surplus) shows only its
  // remainder. Fall back to the full line qty for older API payloads.
  const perUnit = Math.max(1, parseInt(ppq, 10) || 1);
  const maxQty = Math.floor(((part.remaining_qty ?? part.quantity) || 0) / perUnit);
  const [qty, setQty] = React.useState(String(maxQty));
  const [dest, setDest] = React.useState('');
  const key = `assign_${part.part_id}`;
  const onWip = part.on_wip || [];
  const doAssign = async () => {
    if (!dest || maxQty < 1) return;
    const n = Math.min(Math.max(parseInt(qty, 10) || 0, 1), maxQty);
    let destPalletId = null, destBoxId = null, asPalletDirect = false;
    if (dest === newPalletValue) {
      const res = await onCreatePallet('40x48');
      const newId = res && (res.pallet_id ?? res.pallet?.pallet_id);
      if (!newId) return;  // create failed — error already surfaced by the handler
      destPalletId = newId;
      asPalletDirect = true;
    } else {
      const opt = destOptions.find(o => o.value === dest);
      if (!opt) return;
      if (opt.box_id) destBoxId = opt.box_id;
      else { destPalletId = opt.pallet_id; asPalletDirect = !!opt.as_pallet_direct; }
    }
    const body = { part_id: part.part_id, quantity: n * perUnit, ...(extraBody || {}) };
    if (destBoxId) body.dest_box_id = destBoxId;
    else { body.dest_pallet_id = destPalletId; body.as_pallet_direct = asPalletDirect; }
    await onManualAssign(body);
    setDest('');
    setQty(String(maxQty));
  };
  return (
    <div className="d-flex align-items-center gap-1 mt-1 flex-wrap small">
      <span style={{ minWidth: 120 }}>
        <ProductIdLink productId={part.product_id} partId={part.part_id} onInspect={onInspectPart} />
      </span>
      <span className="text-muted text-truncate" style={{ maxWidth: 200 }}>{part.description || ''}</span>
      {(part.assigned_qty || 0) > 0 && (
        <span className="text-muted fst-italic">({part.assigned_qty} of {part.quantity} already packed)</span>
      )}
      <input
        type="number" className="form-control form-control-sm" style={{ maxWidth: 64 }}
        min={1} max={maxQty} value={qty} disabled={!!busy}
        onChange={(e) => setQty(e.target.value)}
        title={perUnit > 1 ? `Assemblies to assign (max ${maxQty}, ${perUnit} pcs each)` : `Quantity to assign (max ${maxQty})`}
      />
      <span className="text-muted">→</span>
      <select
        className="form-select form-select-sm" style={{ maxWidth: 230 }}
        value={dest} disabled={!!busy}
        onChange={(e) => setDest(e.target.value)}
      >
        <option value="">Assign to…</option>
        {destOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        <option value={newPalletValue}>＋ New 40×48 pallet (stack here)</option>
      </select>
      <button
        className="btn btn-sm btn-success"
        disabled={!!busy || !dest || !qty || parseInt(qty, 10) < 1 || maxQty < 1}
        onClick={doAssign}
      >
        {busy === key
          ? <><span className="spinner-border spinner-border-sm me-1"></span>Assigning…</>
          : <><i className="bi bi-plus-circle me-1"></i>Assign</>}
      </button>
      <SOStagingOnWip onWip={onWip} busy={busy} onClearWip={onClearWip} />
    </div>
  );
};


/** Building pallets / boxes a part can be packed into (never a WIP / holding pallet, never a sealed box). */
export function packDestinations(plan) {
  const opts = [];
  for (const p of (plan.pallets || [])) {
    if (p.status !== 'Building' || p.is_wip) continue;
    const pLabel = p.short_id || `P${p.pallet_num}`;
    opts.push({ value: `newstack:${p.pallet_id}`, pallet_id: p.pallet_id, as_pallet_direct: true, label: `${pLabel} — new stack` });
    for (const b of (p.boxes || [])) {
      if (b.status === 'Sealed') continue;
      const kind = b.is_pallet_direct ? 'S' : 'B';
      const displayNum = b.display_box_num ?? b.box_num;
      const suffix = b.is_pallet_direct ? ' (stack)' : (b.sku ? ` (${b.sku})` : '');
      opts.push({ value: `box:${b.box_id}`, box_id: b.box_id, label: `${pLabel} / ${b.short_id || `${kind}${displayNum}`}${suffix}` });
    }
  }
  return opts;
}

const NEW_PALLET = '__new_pallet__';

/** What a holding-pallet card's Pack into tells manual-assign about where the pieces come from (the card can show an
 *  assembly row AND a raw row of the same primary part): this pallet, and the assembly stack or the raw pieces. */
export function packSource(pallet, row) {
  return row.isAssembly
    ? { from_wip_pallet_id: pallet.pallet_id, from_wip_assembly_id: row.assembly_id }
    : { from_wip_pallet_id: pallet.pallet_id, from_wip_raw: true };
}

/** One holding pallet's card. `onMove` opens Move pieces for it (absent: no button). */
export const HoldingPalletCard = ({
  plan, pallet, destOptions, busy = '', locked = false, openProblems = [],
  onPromote, onManualAssign, onPackFromHolding, onCreatePallet, onInspectPart, onClearWip, onFind, onMove,
}) => {
  const [packing, setPacking] = React.useState(false);
  const name = pallet.short_id || `pallet ${pallet.pallet_id}`;
  const st = holdingStationState(pallet);
  const parts = holdingPalletParts(pallet, plan);
  const promote = promoteState(pallet);
  const promoting = busy === `promote_${pallet.pallet_id}`;
  return (
    <div className="border rounded p-2 mb-2 bg-white" data-testid={`holding-pallet-${pallet.pallet_id}`}>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className="fw-bold font-monospace">{name}</span>
        <span
          className={`badge ${st.ready ? 'bg-success' : 'bg-warning text-dark'}`}
          data-testid={`holding-station-${pallet.pallet_id}`}
          title={st.ready
            ? 'No in-house step left: ship it as it is, or pack it.'
            : `Its pieces still owe ${st.station}: the pallet goes there next. Its label and beacon read ${st.label}.`}
        >
          {st.label}
        </span>
        <span className="small text-muted">
          Holding pallet · laser task {pallet.wip_task_id}
          {pallet.wip_master_job_num != null ? ` · job ${pallet.wip_master_job_num}` : ''}
        </span>
        {pallet.beacon_id && (
          <span
            className="badge bg-light text-dark border font-monospace"
            title={pallet.beacon_light ? `${pallet.beacon_id} is on this pallet and flashing` : `${pallet.beacon_id} is on this pallet`}
          >
            {pallet.beacon_light && (
              <span className="d-inline-block rounded-circle bg-success me-1" style={{ width: 7, height: 7 }} aria-label="lit" />
            )}
            {pallet.beacon_id}
          </span>
        )}
        {pallet.beacon_id && onFind && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary py-0"
            disabled={!!busy}
            onClick={() => onFind(pallet.pallet_id)}
            title={`Light ${pallet.beacon_id} and show where ${name} is`}
          >
            <i className="bi bi-broadcast me-1"></i>Find
          </button>
        )}
        <PalletCheckLink palletId={pallet.pallet_id} openProblems={openProblems} compact />
        {onMove && !locked && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary py-0"
            data-testid={`holding-move-${pallet.pallet_id}`}
            disabled={!!busy}
            onClick={() => onMove(pallet.pallet_id)}
            title={`Combine ${name} with another of this order's holding pallets, or split it onto a new one (operator PIN)`}
          >
            <i className="bi bi-arrow-left-right me-1"></i>Move pieces
          </button>
        )}
      </div>

      <ul className="list-unstyled small mb-1 mt-1">
        {parts.map((r) => (
          <li key={r.key} className="d-flex align-items-center gap-2 flex-wrap" data-testid={`holding-part-${pallet.pallet_id}-${r.key}`}>
            {r.isAssembly ? (
              <span style={{ minWidth: 120 }} title={`${r.cut} × ${r.product_id || 'primary part'} (${r.ppq} per assembly)`}>
                <span className="badge bg-success me-1">ASSY</span>
                <strong>{piecesText(r)}</strong>
              </span>
            ) : (
              <>
                <span style={{ minWidth: 120 }}>
                  <ProductIdLink productId={r.product_id} partId={r.part_id} onInspect={onInspectPart} />
                </span>
                <span className="text-muted text-truncate" style={{ maxWidth: 220 }}>{r.description || ''}</span>
                <span className={r.cutting > 0 ? 'text-warning-emphasis' : ''}>{piecesText(r)}</span>
              </>
            )}
          </li>
        ))}
        {!parts.length && <li className="text-muted">Nothing on it yet.</li>}
      </ul>

      <div className="d-flex align-items-center gap-2 flex-wrap">
        {promote.offered && (
          <button
            type="button"
            className={`btn btn-sm ${promote.override ? 'btn-warning' : 'btn-primary'}`}
            data-testid={`holding-promote-${pallet.pallet_id}`}
            disabled={!!busy || !promote.enabled}
            title={promote.override
              ? `${promote.why} A manager can let it through (manager PIN + reason).`
              : (promote.enabled ? `${name} becomes the shipping pallet: its pieces and beacon stay on it.` : promote.why)}
            onClick={() => onPromote && onPromote(pallet.pallet_id)}
          >
            {promoting
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Shipping as it is…</>
              : <><i className="bi bi-box-arrow-right me-1"></i>Ship this pallet as it is</>}
          </button>
        )}
        {!locked && (
          <button
            type="button"
            className={`btn btn-sm ${packing ? 'btn-secondary' : 'btn-outline-secondary'}`}
            data-testid={`holding-pack-${pallet.pallet_id}`}
            disabled={!!busy && !packing}
            aria-expanded={packing}
            onClick={() => setPacking((v) => !v)}
          >
            <i className="bi bi-box-seam me-1"></i>Pack into…
          </button>
        )}
        {onClearWip && (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger py-0 ms-auto"
            disabled={!!busy}
            title="Only when these pieces went somewhere JobQ did not record (packed by hand, scrapped): every stack on the holding pallet goes to 0 and the pallet ends."
            onClick={() => onClearWip(pallet.pallet_id, name)}
          >
            {busy === `wip_clear_${pallet.pallet_id}`
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Clearing…</>
              : 'Clear WIP'}
          </button>
        )}
      </div>
      {promote.offered && promote.override && (
        <div className="small text-warning-emphasis mt-1" data-testid={`holding-promote-why-${pallet.pallet_id}`}>
          <i className="bi bi-exclamation-triangle me-1"></i>{promote.why} A manager can let it through.
        </div>
      )}
      {promote.offered && !promote.enabled && promote.why && (
        <div className="small text-muted mt-1" data-testid={`holding-promote-why-${pallet.pallet_id}`}>
          <i className="bi bi-info-circle me-1"></i>{promote.why}
        </div>
      )}

      {packing && !locked && (
        <div className="mt-1 ps-2 border-start" data-testid={`holding-pack-rows-${pallet.pallet_id}`}>
          {parts.map((r) => (
            <SOStagingAssignRow
              key={r.key}
              part={{
                part_id: r.part_id, product_id: r.isAssembly ? `${r.assembly_name} (assembly)` : r.product_id,
                description: r.isAssembly ? `${rowUnits(r)} on ${name}` : r.description,
                quantity: r.cut, remaining_qty: r.cut, assigned_qty: 0, on_wip: [],
              }}
              extraBody={packSource(pallet, r)}
              ppq={r.isAssembly ? r.ppq : 1}
              destOptions={destOptions}
              newPalletValue={NEW_PALLET}
              busy={busy}
              onManualAssign={onPackFromHolding || onManualAssign}
              onCreatePallet={onCreatePallet}
              onInspectPart={onInspectPart}
            />
          ))}
          {!parts.some((r) => rowUnits(r) >= 1) && <div className="small text-muted">Nothing is cut yet: pieces can be packed once they are off the laser.</div>}
        </div>
      )}
    </div>
  );
};

/** The Staging queue's view on a plan with holding pallets in play. `palletChecks`: usePalletChecks' answer (the
 *  page's, shared with the pallet cards). `onMoved`: re-read the plan after a Move pieces (absent: no Move pieces). */
export const PrepareShipment = ({
  plan, busy = '', palletChecks = {}, onPromote, onManualAssign, onPackFromHolding, onCreatePallet, onInspectPart,
  onReopenPlan, onClearWip, onFind, onMoved,
}) => {
  const [moving, setMoving] = React.useState(null);     // the holding pallet whose Move pieces dialog is open
  const destOptions = React.useMemo(() => packDestinations(plan), [plan]);
  const cards = holdingPallets(plan);
  const loose = stagedOffHolding(plan);
  const isComplete = plan.status === 'Complete';
  const byStation = new Map();
  for (const it of loose) {
    const s = it.next_station || 'Next station';
    if (!byStation.has(s)) byStation.set(s, []);
    byStation.get(s).push(it);
  }
  return (
    <div className="border border-primary rounded p-2 mb-3" style={{ background: '#f4f8ff' }} data-testid="prepare-shipment">
      <div className="fw-bold mb-1 text-primary">
        <i className="bi bi-truck me-1"></i>
        Prepare shipment
        <span className="badge bg-primary ms-2">{cards.length} holding pallet{cards.length === 1 ? '' : 's'}</span>
      </div>
      <div className="small text-muted mb-2">
        Every part goes on a holding pallet at the laser and travels on it to each station. For each holding pallet:
        <strong> ship it as it is</strong> (it becomes the shipping pallet, nothing is re-handled) or
        <strong> pack it</strong> into new pallets or boxes. Only cut pieces can be packed or shipped.
      </div>
      {isComplete && (
        <div className="alert alert-warning py-2 small mb-2 d-flex align-items-center gap-2 flex-wrap">
          <i className="bi bi-lock-fill"></i>
          <span>This plan is marked <strong>Complete</strong>, so packing is locked. Reopen it to pack these pieces.</span>
          {onReopenPlan && (
            <button className="btn btn-sm btn-warning" disabled={!!busy} onClick={onReopenPlan}>
              {busy === 'reopen_plan'
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Reopening…</>
                : <><i className="bi bi-unlock me-1"></i>Reopen plan</>}
            </button>
          )}
        </div>
      )}
      {cards.map((p) => (
        <HoldingPalletCard
          key={p.pallet_id}
          plan={plan}
          pallet={p}
          destOptions={destOptions}
          busy={busy}
          locked={isComplete}
          onPromote={onPromote}
          onManualAssign={onManualAssign}
          onPackFromHolding={onPackFromHolding}
          onCreatePallet={onCreatePallet}
          onInspectPart={onInspectPart}
          onClearWip={onClearWip}
          onFind={onFind}
          openProblems={openProblemsFor(palletChecks, p.pallet_id)}
          onMove={onMoved ? setMoving : undefined}
        />
      ))}
      {moving != null && (
        <HoldingMoveDialog show palletId={moving} onClose={() => setMoving(null)} onMoved={onMoved} />
      )}
      {loose.length > 0 && (
        <div className="mt-2" data-testid="prepare-shipment-loose">
          <div className="small fw-semibold mb-1">Parts not on a holding pallet: pick a pallet once they are ready.</div>
          {Array.from(byStation.entries()).map(([station, items]) => (
            <div key={station} className="mb-1">
              <span className="badge bg-warning text-dark me-1">→ {String(station).toUpperCase()}</span>
              {items.map((it) => (
                isComplete
                  ? (
                    <div key={it.part_id} className="d-flex align-items-center gap-2 mt-1 small text-muted">
                      <ProductIdLink productId={it.product_id} partId={it.part_id} onInspect={onInspectPart} />
                      <span>× {(it.remaining_qty ?? it.quantity) || 0}</span>
                    </div>
                  )
                  : (
                    <SOStagingAssignRow
                      key={it.part_id}
                      part={it}
                      destOptions={destOptions}
                      newPalletValue={NEW_PALLET}
                      busy={busy}
                      onManualAssign={onManualAssign}
                      onCreatePallet={onCreatePallet}
                      onInspectPart={onInspectPart}
                    />
                  )
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
